import { FormEditorTypes } from "@/components/forms/form-editor.config.ts";
import { ApiFormEdit, ApiFormElementEdit } from "@/types/forms/form-edit.types.ts";
import { ApiFormViewElement } from "@/types/forms/form-view.types.ts";
import { ApiFormElementType, ApiFormType } from "@/types/forms/form.types.ts";
import { createContext, useContext } from "react";
import { useForm } from "react-hook-form";

export interface ElementIndex {
  pageIndex: number;
  sectionIndex: number;
  elementIndex: number;
}

interface FormEditorContextType {
  formId: number;
  formType: ApiFormType;
  formEditorType: FormEditorTypes;
  readOnly: boolean;
  formValues: ApiFormEdit;
  selectedElement: ElementIndex | null;
  setSelectedElement: (element: ElementIndex | null) => void;
  toggleSelectedElement: (pageIndex: number, sectionIndex: number, elementIndex: number) => void;
  handleAddPage: () => void;
  handleAddSection: (pageIndex?: number) => void;
  handleAddElement: (
    type: ApiFormElementType,
    position?: { pageIndex?: number; sectionIndex?: number; elementIndex?: number },
    element?: ApiFormElementEdit
  ) => void;
  handleAddScale: (position?: {
    pageIndex?: number;
    sectionIndex?: number;
    elementIndex?: number;
  }) => void;
  handleAddGoalScale: (position?: {
    pageIndex?: number;
    sectionIndex?: number;
    elementIndex?: number;
  }) => void;
  handleSaveForm: () => Promise<void>;
  openEditElement: (pageIndex: number, sectionIndex?: number, elementIndex?: number) => void;
  methods: ReturnType<typeof useForm<ApiFormEdit>>;
  getElementView: (elementId: number) => ApiFormViewElement | null;
  handleSaveAsTemplate: () => void;
  handleStartFromScratch: () => void;
  handleDuplicateElement: (pageIndex: number, sectionIndex: number, elementIndex: number) => void;
  triggerFormUpdate: () => void;
}

export const FormEditorContext = createContext<FormEditorContextType | null>(null);

export const useFormEditor = () => {
  const context = useContext(FormEditorContext);
  if (!context) {
    throw new Error("useFormEditor must be used within a FormEditorProvider");
  }
  return context;
};
